<script setup>
	import {
		ref,
		computed,
	} from 'vue';
	import axios from "axios";
	import { useStore } from 'vuex';
	import { DsIconButton, DsIcon, DsButton, DsText, dsUseIsMobile } from "@coloplast/design-system";
	import { track_bookmark_event } from 'shared/services/tracking/contentTrackingService';

	const store = useStore();

	const { isMobile } = dsUseIsMobile();

	const
		pageId = getPageId(),
		language = computed(() => store.getters.getCulture),
		bookmarksApi = computed(() => `/api/myhcpprofile/bookmark/${language.value}/${pageId}`),
		isUserLoggedIn = computed(() => store.getters.getIsUserLoggedIn);

	const
		bookmarksInfoModel = ref({}),
		showInfoModal = ref(false),
		bookmarkAction = ref('get'),
		iconName = ref('bookmark'); //bookmark-filled

	const props = defineProps({
		shareModel: {
			type: Object,
			default: () => { },
		},
		printModel: {
			type: Object,
			default: () => { },
		},
		shareUrl: {
			type: String,
		},
		addBookmark: {
			type: Boolean,
			default: () => false,
		},
		bookmarkLabel: {
			type: String,
		},
	});

	// Functions
	function printPage() {
		window.print();
	}

	function sharePage() {
		window.location.href = `mailto:?subject=${props.shareModel.BaseViewModel.ShareByEmailSubjectText}&body=${props.shareUrl}`;
	}

	async function copyLink() {
		const copyLinkText = document.getElementById('copyLink').value;
		if (navigator.clipboard) {
			try {
				await navigator.clipboard.writeText(copyLinkText);
			} catch (err) {
				console.error(err);
			}
		}
	}

	function getPageId() {
		const e = window.dataLayer?.find((n) => Object.hasOwn(n, 'PageID'));
		return e ? e.PageID : '';
	}

	function setShowModal() {
		showInfoModal.value = !showInfoModal.value;
	}

	function bookmarksAction() {
		store.dispatch('showSpinner');
		axios[bookmarkAction.value](bookmarksApi.value).then(response => {
			if (response?.status === 200) {
				if (bookmarkAction.value === 'get') {
					if (response.data) {
						iconName.value = 'bookmark-filled';
						bookmarkAction.value = 'delete';
					}
					else {
						bookmarkAction.value = 'post';
					}
				}
				else if (bookmarkAction.value === 'delete') {
					bookmarkAction.value = 'post';
					iconName.value = 'bookmark';
				}
				//if post
				else {
					bookmarkAction.value = 'delete';
					iconName.value = 'bookmark-filled';

					//if that's first added bookmark
					if (response?.data) {
						bookmarksInfoModel.value = response.data;
						showInfoModal.value = true;
					}
					track_bookmark_event();
				}
			}
		})
			.catch(error => {
				if (error?.response?.status == 401) {
					store.commit('setBookmarksConnected', true);
					store.commit('setContentGateIsShown', true);
				}
				else {
					console.warn(error);
				}
			})
			.finally(() => {
				store.dispatch('hideSpinner');
			});
	}

	//we should check if that page is aleready bookmarked only in case of user is logged in
	if (props.addBookmark && isUserLoggedIn.value) {
		bookmarksAction();
	}
</script>

<template>
	<div class="c-share-print__wrapper">
		<!--Share-->
		<template v-if="shareModel.BaseViewModel && shareModel.BaseViewModel.EnableSharing">
			<input type="checkbox"
				   class="hidden"
				   id="share-button" />
			<label for="share-button"
				   class="c-share-print__sharebutton"
				   data-testid="share">
				<template v-if="!isMobile">
					<DsIconButton icon-name="share-ios" size="md" variant="ghost-neutral" class="with-icon c-share-print__noactionbutton"></DsIconButton>
					<span class="c-share-print__sharelabel"
						  v-html="shareModel.BaseViewModel.ShareLabel">
					</span>
				</template>

				<template v-else>
					<DsButton icon-start="share-ios" size="sm" class="c-share-print__noactionbutton"><span v-html="shareModel.BaseViewModel.ShareLabel"></span></DsButton>
				</template>
			</label>
		</template>
		<!--Bookmark-->
		<template v-if="addBookmark">
			<a v-if="!isMobile"
			   class="c-share-print__printlink"
			   title="bookmark"
			   >
				<DsIconButton :icon-name="iconName" size="md" variant="ghost-neutral" class="with-icon" :clickHandler="bookmarksAction" data-testid="bookmark"></DsIconButton>
				<span class="c-share-print__sharelabel"
					  v-if="bookmarkLabel"
					  v-html="bookmarkLabel">
				</span>
			</a>
			<DsButton v-else :icon-start="iconName" size="sm" :clickHandler="bookmarksAction" data-testid="bookmark"><span v-html="bookmarkLabel"></span></DsButton>
		</template>
		<!--Print-->
		<template v-if="printModel && printModel.BaseViewModel && printModel.BaseViewModel.EnablePrinting">
			<a v-if="!isMobile"
			   class="c-share-print__printlink"
			   title="print"
			   >
				<DsIconButton icon-name="print" size="md" variant="ghost-neutral" class="with-icon" :clickHandler="printPage" data-testid="print"></DsIconButton>
				<span class="c-share-print__sharelabel"
					  v-html="printModel.BaseViewModel.PrintLabel">
				</span>
			</a>
			<DsButton v-else icon-start="print" size="sm" :clickHandler="printPage" class="c-share-print__hiddenicon" data-testid="print"><span v-html="printModel.BaseViewModel.PrintLabel"></span></DsButton>
		</template>
		<template v-if="shareModel.BaseViewModel && shareModel.BaseViewModel.EnableSharing">
			<div class="c-modal__mask c-share-print__modal c-modal__mask--grey">
				<label for="share-button"
					   class="full-width-label"
					   id="label-close-modal">
				</label>
				<div class="c-modal__content-wrapper c-share-print__modalwrapper">
					<label for="share-button"
						   class="c-modal__close--big"
						   aria-label="Close">
					</label>
					<h2 v-html="shareModel.BaseViewModel.ShareModalHeadline"></h2>
					<h3 v-html="shareModel.BaseViewModel.ShareByEmailHeading"></h3>
					<p class="c-share-print__sharedescription" v-html="shareModel.BaseViewModel.ShareByEmailDescription"></p>
					<label for="share-button"
						   class="e-button e-button--filled send-button small"
						   v-html="shareModel.BaseViewModel.ShareByEmailButtonLabel"
						   @click="sharePage()"
						   data-testid="share-page">
					</label>
					<h3 v-html="shareModel.BaseViewModel.ShareByLinkHeadline"></h3>
					<div class="c-share-print__copybox">
						<button class="c-share-print__copyLink"
								@click="copyLink()"
								data-testid="copy-link">
							{{ shareModel.BaseViewModel.ShareByLinkButtonLabel }}
						</button>
						<input type="text"
							   id="copyLink"
							   readonly
							   :value="shareUrl" />
					</div>
				</div>
			</div>
		</template>
	</div>
	<!--Bookmarks Info modal if bookmark is added first time-->
	<div class="c-modal__mask" v-if="showInfoModal" @click="setShowModal()" @kedown.enter="setShowModal()" @keydown.escape="setShowModal()">
		<div class="c-modal__wrapper --fixed-size">
			<DsIcon v-if="bookmarksInfoModel.icon"
					:iconName="bookmarksInfoModel.icon"
					size="xl"
					class="c-modal__roundedicon" />
			<DsIconButton class="c-modal__close--from-ds-icon-button"
						  aria-label="Close button"
						  icon-name="close"
						  variant="ghost-neutral" />
			<DsText v-if="bookmarksInfoModel.headline"
					html-element="div"
					text-style="regular"
					text-type="heading-3xl"
					class="c-modal__headline">
					<div v-html="bookmarksInfoModel.headline" />
			</DsText>
			<DsText v-if="bookmarksInfoModel.description"
					html-element="div"
					text-style="regular"
					text-type="body-md">
					<div v-html="bookmarksInfoModel.headline" />
			</DsText>
			<div class="c-modal__buttons c-modal__hcp-buttons">
				<DsButton variant="primary" size="md" type="button" v-if="bookmarksInfoModel.closeButtonText">{{ bookmarksInfoModel.closeButtonText }}</DsButton>
				<DsButton @click.stop :variant="bookmarksInfoModel.closeButtonText ? 'secondary' : 'primary'" size="md" type="button" v-if="bookmarksInfoModel.redirectButtonUrl" :href="bookmarksInfoModel.redirectButtonUrl">{{ bookmarksInfoModel.redirectButtonText }}</DsButton>
            </div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@import "shared/styles/settings/settings";

	.c-modal__wrapper {
		&.--fixed-size {
			max-width: 696px;
			width: 95%;
			padding: ds-space(14);
			border-radius: 8px;
			text-align: center;
			overflow: hidden;
		}
	}

	.c-modal__close--from-ds-icon-button {
		top: ds-space(6);
		right: ds-space(6);
	}

	.c-modal {
		&__roundedicon {
			width: 104px;
			height: 104px;
			padding: ds-space(9);
			border-radius: 50%;
			background-color: ds-color('bg', 'decorative-3-muted');
			color: ds-color-action('primary', 'default', 'bg-color');
			margin-bottom: ds-space(8);
		}

		&__headline {
			margin: ds-space(4) 0;
		}

		&__buttons {
			display: flex;
			padding-top: ds-space(10);
			column-gap: ds-space(8);
			justify-content: center;
			flex-wrap: wrap;
		}
	}
</style>
